// 직원 로그인 화면(시안 #login, 계약 §2). 단계: login · change(첫 로그인 비밀번호 변경) · signup · reset(링크 요청) · confirm(메일 링크 화면 — 시안에 없어 새로 만듦, §16).
// 시연용 계정 칩·가입 코드 힌트는 두지 않는다(§16).
import { useEffect, useState } from "react";
import * as authApi from "./authApi";
import { saveLogin, pwOk } from "./auth";
import { errorText } from "../inquiry/v6Api";
import { showToast } from "../../utill/utill";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PW_MSG = "비밀번호는 8자 이상, 숫자와 글자를 섞어주세요.";
const MISMATCH = "두 비밀번호가 다릅니다.";
const TRIES = 5;

// 서버 오류를 한 줄로: 남은 횟수 (n/5), 잠금은 남은 분
function failText(e) {
    const d = e?.response?.data?.detail;
    const msg = errorText(e);
    if (d?.code === "account_locked" || d?.code === "signup_locked") {
        const min = Math.max(1, Math.ceil((d.retry_after || 0) / 60));
        return `${msg} ${min}분 뒤 다시 시도하세요.`;
    }
    if (typeof d?.remaining_attempts === "number") return `${msg} (${TRIES - d.remaining_attempts}/${TRIES})`;
    return msg;
}

const Err = ({ msg }) => <div className={`err ${msg ? "on" : ""}`} style={{ margin: "-6px 0 10px" }}>{msg}</div>;
const Field = ({ id, label, children }) => <div className="fld"><label htmlFor={id}>{label}</label>{children}</div>;
const inp = { width: "100%", height: 40 };

// 새 비밀번호 두 칸 — 첫 로그인 변경과 재설정 링크 화면이 같이 쓴다
function NewPassword({ title, bullets, button, onSubmit, busy }) {
    const [a, setA] = useState(""), [b, setB] = useState(""), [err, setErr] = useState("");
    const go = () => {
        if (!pwOk(a)) { setErr(PW_MSG); return; }
        if (a !== b) { setErr(MISMATCH); return; }
        setErr("");
        onSubmit(a).catch((e) => setErr(failText(e)));
    };
    return (
        <div>
            <div className="ai" style={{ marginBottom: 14 }}><div className="ai-h"><i className="fa-solid fa-key"></i>{title}</div><ul>{bullets.map((t) => <li key={t}>{t}</li>)}</ul></div>
            <Field id="lgNew1" label="새 비밀번호"><input id="lgNew1" className="inp" type="password" autoComplete="new-password" style={inp} value={a} onChange={(e) => setA(e.target.value)} /></Field>
            <Field id="lgNew2" label="새 비밀번호 확인"><input id="lgNew2" className="inp" type="password" autoComplete="new-password" style={inp} value={b} onChange={(e) => setB(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} /></Field>
            <Err msg={err} />
            <button type="button" className="btn btn-pri" disabled={busy} onClick={go}><i className="fa-solid fa-check"></i>{button}</button>
        </div>
    );
}

// initialStep="change": 첫 로그인 변경 전 토큰으로 다시 열었을 때(저장된 임시 토큰으로 바로 변경). notice: 끊긴 이유 한 줄
export default function LoginScreen({ onDone, resetToken = null, initialStep = null, notice = "" }) {
    const [step, setStep] = useState(resetToken ? "confirm" : initialStep || "login");
    const [busy, setBusy] = useState(false);
    const [email, setEmail] = useState(""), [pw, setPw] = useState(""), [lgErr, setLgErr] = useState("");
    const [su, setSu] = useState({ name: "", team: "", email: "", pw: "", pw2: "", code: "" }), [suErr, setSuErr] = useState("");
    const [opts, setOpts] = useState(null);
    const [rsMail, setRsMail] = useState(""), [rsErr, setRsErr] = useState("");

    // 요청을 하나로 감싼다: 진행 중 버튼 막기, 오류는 그 단계의 줄에
    const run = async (fn, setErr) => {
        if (busy) return;
        setBusy(true); setErr("");
        try { await fn(); } catch (e) { setErr(failText(e)); } finally { setBusy(false); }
    };
    const finish = (out) => { saveLogin(out); onDone(out.user); };

    const doLogin = () => run(async () => {
        const out = await authApi.login(email.trim(), pw);
        saveLogin(out); // 첫 로그인이면 이 토큰으로 변경 요청을 보낸다
        if (out.user?.must_change_password) { setStep("change"); return; }
        onDone(out.user);
    }, setLgErr);

    const doChange = async (newPw) => {
        setBusy(true);
        try { finish(await authApi.changePassword(newPw)); } finally { setBusy(false); }
    };

    useEffect(() => {
        if (step !== "signup" || opts) return;
        authApi.signupOptions().then((o) => { setOpts(o); setSu((p) => ({ ...p, team: p.team || o.teams?.[0] || "" })); }).catch(() => setOpts({ teams: [], password_rule: "" }));
    }, [step, opts]);

    const doSignup = () => {
        const name = su.name.trim(), mail = su.email.trim();
        if (!name) { setSuErr("이름을 입력해 주세요."); return; }
        if (!EMAIL_RE.test(mail)) { setSuErr("이메일 형식을 확인해 주세요."); return; }
        if (!pwOk(su.pw)) { setSuErr(PW_MSG); return; }
        if (su.pw !== su.pw2) { setSuErr(MISMATCH); return; }
        if (!su.code.trim()) { setSuErr("회사 가입 코드를 입력해 주세요."); return; }
        run(async () => finish(await authApi.signup({ name, team: su.team, email: mail, password: su.pw, join_code: su.code.trim() })), setSuErr);
    };

    const doReset = () => {
        const mail = rsMail.trim();
        if (!EMAIL_RE.test(mail)) { setRsErr("이메일 형식을 확인해 주세요."); return; }
        run(async () => {
            await authApi.requestReset(mail);
            setEmail(mail); setStep("login");
            showToast("등록된 이메일이면 재설정 링크를 보냈습니다. 메일의 링크를 열어 새 비밀번호를 정하세요.", "success");
        }, setRsErr);
    };

    const doConfirm = async (newPw) => {
        setBusy(true);
        try {
            await authApi.confirmReset(resetToken, newPw);
            showToast("비밀번호를 바꿨습니다. 새 비밀번호로 로그인하세요.", "success");
            setStep("login");
        } finally { setBusy(false); }
    };

    const back = (e) => { e.preventDefault(); setStep("login"); setSuErr(""); setRsErr(""); };
    const set = (k) => (e) => setSu((p) => ({ ...p, [k]: e.target.value }));

    return (
        <div className="login on">
            <div className="lbox">
                <div className="sb-logo" style={{ width: 44, height: 44, fontSize: 21 }}>G</div>
                <h2>가람포스텍 AI 관리센터</h2>
                <p>{step === "signup" ? "직원 계정을 만듭니다" : step === "reset" ? "비밀번호 재설정" : step === "confirm" ? "새 비밀번호를 정합니다" : "직원 계정으로 로그인하세요"}</p>

                {step === "login" && (
                    <div>
                        {notice && <div className="err on" role="status" style={{ margin: "0 0 12px" }}>{notice}</div>}
                        <Field id="lgMail" label="이메일"><input id="lgMail" className="inp" placeholder="name@company.com" autoComplete="username" style={inp} value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && document.getElementById("lgPw")?.focus()} /></Field>
                        <Field id="lgPw" label="비밀번호"><input id="lgPw" className="inp" type="password" placeholder="8자 이상" autoComplete="current-password" style={inp} value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} /></Field>
                        <Err msg={lgErr} />
                        <button type="button" className="btn btn-pri" disabled={busy} onClick={doLogin}><i className="fa-solid fa-right-to-bracket"></i>로그인</button>
                        <div className="lglinks">
                            <a href="#signup" onClick={(e) => { e.preventDefault(); setStep("signup"); }}><i className="fa-solid fa-user-plus"></i> 계정 만들기</a>
                            <a href="#reset" onClick={(e) => { e.preventDefault(); setRsMail(email); setStep("reset"); }}>비밀번호를 잊으셨나요?</a>
                        </div>
                        <div className="note" style={{ textAlign: "center", marginTop: 12 }}>5회 실패 시 10분 동안 잠깁니다</div>
                    </div>
                )}

                {step === "change" && (
                    <NewPassword title="첫 로그인입니다. 새 비밀번호를 정해주세요" button="비밀번호 바꾸고 시작" busy={busy} onSubmit={doChange}
                        bullets={["이메일로 받은 임시 비밀번호는 지금까지만 쓸 수 있습니다", "8자 이상, 숫자와 글자를 섞어주세요"]} />
                )}

                {step === "signup" && (
                    <div>
                        <div className="grid2">
                            <Field id="suName" label={<>이름 <span className="req">*</span></>}><input id="suName" className="inp" style={inp} value={su.name} onChange={set("name")} /></Field>
                            <Field id="suTeam" label="소속">
                                <select id="suTeam" className="sel" style={inp} value={su.team} onChange={set("team")}>
                                    {(opts?.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>
                        </div>
                        <Field id="suMail" label={<>이메일 <span className="req">*</span></>}><input id="suMail" className="inp" placeholder="로그인 아이디로 씁니다" autoComplete="username" style={inp} value={su.email} onChange={set("email")} /></Field>
                        <div className="grid2">
                            <Field id="suPw" label={<>비밀번호 <span className="req">*</span></>}><input id="suPw" className="inp" type="password" placeholder="8자 이상, 숫자+글자" autoComplete="new-password" style={inp} value={su.pw} onChange={set("pw")} /></Field>
                            <Field id="suPw2" label="비밀번호 확인"><input id="suPw2" className="inp" type="password" autoComplete="new-password" style={inp} value={su.pw2} onChange={set("pw2")} /></Field>
                        </div>
                        <Field id="suCode" label={<>회사 가입 코드 <span className="req">*</span></>}>
                            <input id="suCode" className="inp" placeholder="대표관리자에게 받은 6자리" inputMode="numeric" maxLength={6} style={{ ...inp, letterSpacing: 3 }} value={su.code} onChange={set("code")} onKeyDown={(e) => e.key === "Enter" && doSignup()} />
                            <div className="help">코드가 맞으면 바로 직원 계정으로 문의 관리를 쓸 수 있습니다{opts?.password_rule ? ` · ${opts.password_rule}` : ""}</div>
                        </Field>
                        <Err msg={suErr} />
                        <button type="button" className="btn btn-pri" disabled={busy} onClick={doSignup}><i className="fa-solid fa-check"></i>가입하고 시작</button>
                        <div className="lglinks" style={{ justifyContent: "center" }}><a href="#login" onClick={back}>로그인으로 돌아가기</a></div>
                    </div>
                )}

                {step === "reset" && (
                    <div>
                        <div className="ai" style={{ marginBottom: 14 }}><div className="ai-h"><i className="fa-solid fa-envelope"></i>비밀번호 재설정</div><ul><li>가입한 이메일로 재설정 링크를 보냅니다.</li><li>이메일을 못 받으면 대표관리자에게 말해주세요.</li></ul></div>
                        <Field id="rsMail" label="이메일"><input id="rsMail" className="inp" placeholder="name@company.com" style={inp} value={rsMail} onChange={(e) => setRsMail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doReset()} /></Field>
                        <Err msg={rsErr} />
                        <button type="button" className="btn btn-pri" disabled={busy} onClick={doReset}><i className="fa-solid fa-paper-plane"></i>재설정 링크 보내기</button>
                        <div className="lglinks" style={{ justifyContent: "center" }}><a href="#login" onClick={back}>로그인으로 돌아가기</a></div>
                    </div>
                )}

                {step === "confirm" && (
                    <div>
                        <NewPassword title="메일 링크로 왔습니다. 새 비밀번호를 정해주세요" button="비밀번호 바꾸기" busy={busy} onSubmit={doConfirm}
                            bullets={["링크는 한 번만 쓸 수 있습니다", "8자 이상, 숫자와 글자를 섞어주세요"]} />
                        <div className="lglinks" style={{ justifyContent: "center" }}><a href="#login" onClick={back}>로그인으로 돌아가기</a></div>
                    </div>
                )}
            </div>
        </div>
    );
}
